// More on how to set up stories at: https://storybook.js.org/docs/writing-stories#default-export
import React from 'react';
import Note from './Note';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/Notes/NoteCard',
  component: Note,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Note` component provides a compact and visually structured representation of a note. It displays key details like site name, title, content, date, status, and priority. Priority levels (e.g., Low, Medium, High) is visually indicated by a color strip at the top of the card.\n\n',
      // When & how to use it?
      '\nUse the `Note` component when you need to display individual note details in a card-like layout. It is typically used in a grid or list format to showcase multiple notes in a concise manner.\n\n',
      // Current Usage
      `\nThe \`Note\` component is used within the \`NotesPage\` to display individual note records. It includes the following details:
      \n- Site Name
      \n- Date
      \n- Title
      \n- Content
      \n- Status
      \n- Priority (indicated by a color strip at the top of the card)
      \n- Click Interaction\n\n`,
      // Related Components
      `\n- **[NotesTable](http://localhost:6006/?path=/docs/components-notes-notestable--docs)**: Component that renders multiple Note components in a table view.
      \n- **[NotesPage](http://localhost:6006/?path=/docs/pages-notespage--docs)**: Page where the Note component is used within a grid or list layout.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    siteName: {
      control: { type: 'text' },
      description: 'The name of the site associated with the note.\n\n',
      table: {
        type: { summary: 'string' },
        required: true,
      },
    },
    date: {
      control: { type: 'text' },
      description: 'The date when the note was created or last updated.\n\n',
      table: {
        type: { summary: 'string' },
        required: true,
      },
    },
    title: {
      control: { type: 'text' },
      description: 'The title of the note.\n\n',
      table: {
        type: { summary: 'string' },
        required: true,
      },
    },
    content: {
      control: { type: 'text' },
      description: 'The content or description of the note.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    status: {
      control: { type: 'text' },
      description: 'The status of the note (e.g., Open, Closed).\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    priority: {
      control: { type: 'text' },
      description: 'The priority level of the note (e.g., Low, Medium, High). The priority is indicated by a color strip at the top of the card.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    onClick: {
      control: { type: 'function' },
      description: 'Callback function triggered when the note is clicked.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

const Template = (args) => (
  <div style={{ width: '400px', margin: '0 auto' }}>
    <Note {...args} />
  </div>
);

export const Default = Template.bind({});
Default.args = {
  siteName: 'Site Name (no priority site)',
  date: '06/13/24',
  title: 'Note Title',
  content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor consectetur adipiscing elit, sed do eiusmod asdasdfasdfasdfasd',
  status: 'Open',
  priority: 'no_priority',
};

Default.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to display a single `Note` with sample data. It is typically rendered within a grid or list format on the `NotesPage`.\n\n',
  // Where is it currently used on the product?
  '\nThe `Note` component is used in the grid or list view on the `NotesPage` to display individual note records.\n\n',
);

export const Low = Template.bind({});
Low.args = {
  siteName: 'Site Name (low priority site)',
  date: '06/13/24',
  title: 'Note Title',
  content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididuasdddddnt consectetur adipiscing elit, sed do eiusmod temporaaaaaaaaaaaaaaaaaaaaaaaaa iaaaaaaaaaaaaaaaaaaaancididunt asdasdfasdfasdfasd',
  status: 'Open',
  priority: 'low',
};

export const Medium = Template.bind({});
Medium.args = {
  siteName: 'Site Name (medium priority site)',
  date: '06/13/24',
  title: 'Note Title',
  content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididuasdddddnt consectetur adipiscing elit, sed do eiusmod temporaaaaaaaaaaaaaaaaaaaaaaaaa iaaaaaaaaaaaaaaaaaaaancididunt asdasdfasdfasdfasd',
  status: 'Open',
  priority: 'medium',
};

export const High = Template.bind({});
High.args = {
  siteName: 'Site Name (high priority site)',
  date: '06/13/24',
  title: 'Note Title',
  content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididuasdddddnt consectetur adipiscing elit, sed do eiusmod temporaaaaaaaaaaaaaaaaaaaaaaaaa iaaaaaaaaaaaaaaaaaaaancididunt asdasdfasdfasdfasd',
  status: 'Open',
  priority: 'high',
};
